// web/src/billing/BillingSettings.tsx
//
// /account/billing panel. Renders one of five variants by status; all
// CRUD actions deep-link to Stripe Customer Portal — we never build
// native upgrade/cancel/update-card UI.
import { Button } from "@/components/ui/button";
import { useSubscription, usePortalSession, useCheckoutSuccessPolling } from "./api";
import { SettingsCard } from "@/components/settings/SettingsCard";
import { SettingsLayout } from "@/components/settings/SettingsLayout";


function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}


export function BillingSettings() {
  const sub = useSubscription();
  const portal = usePortalSession();
  useCheckoutSuccessPolling();

  async function openPortal() {
    const res = await portal.mutateAsync({});
    window.location.href = res.url;
  }

  if (!sub.data) {
    return (
      <SettingsLayout title="Billing" description="">
        <SettingsCard title="Loading…" description=""> </SettingsCard>
      </SettingsLayout>
    );
  }

  const { tier, status, trial_ends_at, current_period_end, billing_cadence } = sub.data;
  const planLabel = tier === "free" ? "Free" : `${tier === "pro" ? "Pro" : "Analyst"} ${
    billing_cadence === "annual" ? "Annual" : "Monthly"
  }`;

  return (
    <SettingsLayout
      title="Billing"
      description="Manage your subscription, payment method, and invoices."
    >
      {status === "past_due" && (
        <SettingsCard
          title="Last payment failed"
          description="Update your payment method to keep access."
          className="border-destructive/40"
        >
          <Button onClick={openPortal} variant="destructive">
            Update payment method
          </Button>
        </SettingsCard>
      )}

      <SettingsCard
        title={status === "none" ? "You're on the Free plan" : `Current plan: ${planLabel}`}
        description={
          status === "trialing"
            ? `Trial ends ${fmtDate(trial_ends_at)} — your card will be charged automatically.`
            : status === "active"
              ? `Renews ${fmtDate(current_period_end)}.`
              : status === "canceled"
                ? `Ends ${fmtDate(current_period_end)}. After this date you'll drop to Free.`
                : status === "none"
                  ? "Upgrade to unlock the AI Copilot and premium analyses."
                  : ""
        }
      >
        <div className="flex flex-wrap gap-2">
          {status === "none" ? (
            <Button asChild>
              <a href="https://tapelens.com/pricing">View pricing</a>
            </Button>
          ) : (
            <Button onClick={openPortal}>Manage subscription</Button>
          )}
          {status === "active" && (
            <Button asChild variant="outline">
              <a href="https://tapelens.com/pricing">Switch plan</a>
            </Button>
          )}
          {status === "canceled" && (
            <Button onClick={openPortal} variant="outline">Reactivate</Button>
          )}
        </div>
      </SettingsCard>
    </SettingsLayout>
  );
}
